import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdBuild, MdEdit, MdSearch } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  cellValue,
  matchSelectFilter,
  uniqueColumnOptions,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import {
  equipmentCategoryOptions,
  equipmentStatusOptions,
} from './receptionistData';
import {
  fetchEquipments,
  saveEquipment,
} from '../../services/receptionist/receptionistApi';
import {
  exportEquipmentCsv,
  exportEquipmentExcel,
  exportEquipmentPdf,
  exportEquipments,
  printEquipmentList,
} from './exportEquipments';
import { instantLoadState } from '../../utils/instantLoad';
import { peekGymCache } from '../../services/cache/gymCache';
import { dedupeByName } from '../../utils/dedupeList';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../admin/memberships/memberships.css';
import './members/members.css';

const statusBadge = {
  available: 'owner-badge-success',
  'in-use': 'owner-badge-info',
  maintenance: 'owner-badge-warning',
};

function equipmentStatusLabel(status) {
  if (status === 'in-use') return 'In Use';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
}

function getEmptyEquipmentForm() {
  return {
    name: '',
    companyName: '',
    category: equipmentCategoryOptions[0],
    location: '',
    quantity: '1',
    status: 'available',
    remarks: '',
  };
}

function Equipments() {
  const toast = usePanelToast();
  const equipmentBootstrap = instantLoadState('equipments');
  const [list, setList] = useState(equipmentBootstrap.initialData);
  const [loading, setLoading] = useState(equipmentBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [equipmentForm, setEquipmentForm] = useState(getEmptyEquipmentForm);
  const [editingId, setEditingId] = useState('');
  const [search, setSearch] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: 'all',
    companyName: 'all',
    category: 'all',
    location: 'all',
    quantity: 'all',
    status: 'all',
    remarks: 'all',
  });

  const loadList = useCallback(async () => {
    try {
      setError('');
      if (!peekGymCache('equipments')) setLoading(true);
      setList(dedupeByName(await fetchEquipments()));
    } catch (err) {
      setError(err.message || 'Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filteredList = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matched = list.filter((e) => {
      const matchesSearch = !query || (
        String(e.name || '').toLowerCase().includes(query)
        || String(e.companyName || '').toLowerCase().includes(query)
        || String(e.category || '').toLowerCase().includes(query)
        || String(e.location || '').toLowerCase().includes(query)
        || String(e.remarks || '').toLowerCase().includes(query)
        || String(e.status || '').toLowerCase().includes(query)
        || String(e.quantity ?? '').includes(query)
      );
      return matchesSearch
        && matchSelectFilter(cellValue(e.name), filters.name)
        && matchSelectFilter(cellValue(e.companyName), filters.companyName)
        && matchSelectFilter(cellValue(e.category), filters.category)
        && matchSelectFilter(cellValue(e.location), filters.location)
        && matchSelectFilter(cellValue(e.quantity), filters.quantity)
        && matchSelectFilter(equipmentStatusLabel(e.status), filters.status)
        && matchSelectFilter(cellValue(e.remarks), filters.remarks);
    });
    return dedupeByName(matched);
  }, [list, search, filters]);

  const openModal = () => {
    setFormKey((k) => k + 1);
    setEditingId('');
    setEquipmentForm(getEmptyEquipmentForm());
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormKey((k) => k + 1);
    setEditingId(item.id);
    setEquipmentForm({
      name: item.name || '',
      companyName: item.companyName || '',
      category: item.category || equipmentCategoryOptions[0],
      location: item.location || '',
      quantity: String(item.quantity != null ? item.quantity : 1),
      status: item.status || 'available',
      remarks: item.remarks || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId('');
  };

  const handleSaveEquipment = async (e) => {
    e.preventDefault();
    if (!equipmentForm.name.trim() || !equipmentForm.location.trim()) return;

    const record = {
      name: equipmentForm.name.trim(),
      companyName: equipmentForm.companyName.trim(),
      category: equipmentForm.category,
      location: equipmentForm.location.trim(),
      quantity: Math.max(1, Number(equipmentForm.quantity) || 1),
      status: equipmentForm.status,
      remarks: equipmentForm.remarks.trim(),
    };

    try {
      if (editingId) {
        const updated = await saveEquipment(record, editingId);
        setList((prev) => prev.map((item) => (item.id === editingId ? updated : item)));
        toast.showUpdated('Equipment');
      } else {
        const created = await saveEquipment(record);
        setList((prev) => [created, ...prev]);
        toast.showAdded('Equipment');
      }
      closeModal();
    } catch (err) {
      const message = err.message || 'Failed to save equipment';
      setError(message);
      toast.showError(message);
    }
  };

  return (
    <div>
      <PageHeader title="Equipments" subtitle="Add and manage gym equipment" variant="banner">
        <button type="button" className="owner-btn owner-btn-primary" onClick={openModal}>
          <MdBuild style={{ marginRight: 8 }} />
          Add Equipment
        </button>
      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading equipment...</p>}

      <div className="rec-member-toolbar">
        <div className="rec-member-toolbar-left">
          <div className="rec-member-search-field">
            <MdSearch className="rec-member-search-icon" />
            <input
              type="text"
              className="owner-form-input rec-member-search-input"
              placeholder="Search equipment..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printEquipmentList(filteredList)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportEquipmentCsv(filteredList)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportEquipmentPdf(filteredList)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportEquipmentExcel(filteredList)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportEquipments(filteredList)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Equipment Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => e.name)}
                  allLabel="All Equipment"
                />
                <ColumnFilterHeader
                  label="Company Name"
                  filterKey="companyName"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => e.companyName)}
                  allLabel="All Companies"
                />
                <ColumnFilterHeader
                  label="Category"
                  filterKey="category"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => e.category)}
                  allLabel="All Categories"
                />
                <ColumnFilterHeader
                  label="Location"
                  filterKey="location"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => e.location)}
                  allLabel="All Locations"
                />
                <ColumnFilterHeader
                  label="Quantity"
                  filterKey="quantity"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => e.quantity)}
                  allLabel="All Quantities"
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => equipmentStatusLabel(e.status))}
                  allLabel="All Status"
                />
                <ColumnFilterHeader
                  label="Remarks"
                  filterKey="remarks"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (e) => e.remarks)}
                  allLabel="All Remarks"
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length > 0 ? (
                filteredList.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name || '—'}</td>
                    <td>{item.companyName || '—'}</td>
                    <td>{item.category || '—'}</td>
                    <td>{item.location || '—'}</td>
                    <td>{item.quantity != null ? item.quantity : 1}</td>
                    <td>
                      <span className={`owner-badge ${statusBadge[item.status] || 'owner-badge-info'}`}>
                        {equipmentStatusLabel(item.status)}
                      </span>
                    </td>
                    <td>{item.remarks || '—'}</td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <button
                          type="button"
                          className="rec-member-icon-btn rec-member-icon-btn-edit"
                          onClick={() => openEditModal(item)}
                          aria-label={`Edit ${item.name}`}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text">No equipment matches your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-equipment-title"
          >
            <div className="subscription-modal-header">
              <h2 id="add-equipment-title" className="subscription-modal-title">{editingId ? 'Edit Equipment' : 'Add Equipment'}</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleSaveEquipment}>
              <div className="owner-grid-2">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="equipment-name">Equipment Name</label>
                  <input
                    id="equipment-name"
                    className="owner-form-input"
                    value={equipmentForm.name}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="equipment-company">Company Name</label>
                  <input
                    id="equipment-company"
                    className="owner-form-input"
                    value={equipmentForm.companyName}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, companyName: e.target.value }))}
                    placeholder="e.g. Gold Crest"
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="equipment-category">Category</label>
                  <select
                    id="equipment-category"
                    className="owner-form-select"
                    value={equipmentForm.category}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, category: e.target.value }))}
                  >
                    {equipmentCategoryOptions.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="equipment-location">Location</label>
                  <input
                    id="equipment-location"
                    className="owner-form-input"
                    value={equipmentForm.location}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, location: e.target.value }))}
                    placeholder="e.g. Mall / Cardio Zone"
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="equipment-quantity">Quantity</label>
                  <input
                    id="equipment-quantity"
                    type="number"
                    min="1"
                    step="1"
                    className="owner-form-input"
                    value={equipmentForm.quantity}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, quantity: e.target.value }))}
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="equipment-status">Status</label>
                  <select
                    id="equipment-status"
                    className="owner-form-select"
                    value={equipmentForm.status}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, status: e.target.value }))}
                  >
                    {equipmentStatusOptions.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="equipment-remarks">Remarks</label>
                  <textarea
                    id="equipment-remarks"
                    className="owner-form-input"
                    rows={3}
                    value={equipmentForm.remarks}
                    onChange={(e) => setEquipmentForm((p) => ({ ...p, remarks: e.target.value }))}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="owner-btn owner-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default Equipments;
